/*
 * obf-pdp-v2.js for Stride PDP v2 behaviours.
 *
 * Three independent features, each a no-op when its markup is absent:
 *   1. Colour grouped gallery grid (desktop only, >= 1068px)
 *   2. The sole section insole in / out toggle
 *   3. The lift count weight calculator
 *
 * Loaded with defer from the sections that need it, so it may be included
 * more than once on a page. The OBF_PDP_V2 guard makes that harmless.
 */
(function () {
  'use strict';
  if (window.OBF_PDP_V2) return;
  window.OBF_PDP_V2 = true;

  var GRID_MQ = '(min-width: 1068px)';

  /* ---------------------------------------------------------------
   * 1. Desktop gallery grid
   *
   * THE GALLERY IS OWNED BY AN APP. "Variant Image Automator" (StarApps)
   * renames the theme's <product-slider id="Product-Slider"> to
   * "Product-Slider-old", hides it, and injects a CLONE next to it holding
   * only the selected colour's media, tagged `class="starapps"`. It rebuilds
   * that clone from scratch on every colour change.
   *
   * So the app decides WHICH images show and we only make them a grid. There
   * is no colour filtering left to do here: our old data-obf-color pass is
   * gone, because the app strips the classes it would depend on when it
   * rebuilds. product-media.liquid still stamps data-obf-color and the clone
   * inherits it, which is useful for debugging, but nothing reads it now.
   *
   * What IS still needed is tearing Flickity off the clone: Flickity
   * re-parents every slide into an absolutely positioned
   * .flickity-viewport > .flickity-slider and writes inline position/left/
   * width per cell, which destroys grid flow. destroy() unwraps all of that
   * and restores plain slide DOM, which is exactly what the grid needs.
   *
   * Below 1068px we never touch it, so the mobile carousel is untouched.
   */
  function unflick(slider) {
    if (!slider) return;
    if (!window.matchMedia(GRID_MQ).matches) return;
    // <product-slider> keeps its instance on .flkty (see product.js). The app's
    // clone is a plain element running its own Flickity, so also try the
    // library's own instance registry before giving up.
    var inst = slider.flkty;
    if (!inst && window.Flickity && typeof window.Flickity.data === 'function') {
      inst = window.Flickity.data(slider);
    }
    if (inst && typeof inst.destroy === 'function') {
      try { inst.destroy(); } catch (e) { /* already gone */ }
      slider.flkty = null;
    }
    // Belt and braces: strip anything Flickity or the theme's fade-in left on
    // the element. The markup starts at inline `opacity:0` and is only revealed
    // once Flickity reports ready, and the clone inherits that inline style
    // from the markup it was copied from. We tore Flickity down, so the reveal
    // never fires and a perfectly laid out grid would render invisible.
    // Flickity also pins an inline height (0 right after a colour rebuild).
    slider.classList.remove('flickity-enabled', 'is-draggable');
    slider.removeAttribute('tabindex');
    slider.style.height = '';
    slider.style.opacity = '1';
    slider.style.display = '';
    slider.style.visibility = 'visible';
  }

  function galleryInit() {
    var grid = document.querySelector('.obf-pdp-grid');
    if (!grid) return;

    var container = grid.querySelector('.product-image-container');
    if (!container) return;

    /* The live gallery is whichever slider the app currently owns.
     *
     * Resolve it fresh every time rather than caching a node: the app REPLACES
     * the clone on each colour change, so a cached reference goes stale after
     * the first switch. Prefer the app's clone; fall back to the theme's own
     * slider so the grid still works if the app is absent, disabled, or fails
     * to load. Never return the retired "-old" node, which the app hides.
     */
    function liveSlider() {
      return container.querySelector('.product-images.starapps') ||
             container.querySelector('.product-images:not(#Product-Slider-old)');
    }

    function sync() {
      unflick(liveSlider());
    }

    sync();

    // The app rebuilds asynchronously after a colour change, and Flickity
    // re-inits a tick later still, so one pass is not enough. Re-run over a
    // short window to catch the rebuild whenever it lands.
    function syncSoon() {
      [0, 60, 200, 450, 900].forEach(function (ms) {
        window.setTimeout(sync, ms);
      });
    }

    document.addEventListener('variant:change', syncSoon);
    grid.addEventListener('change', function (e) {
      if (e.target && e.target.tagName === 'INPUT') syncSoon();
    });
    // Swatches are often labels over hidden radios, so a click may land before
    // :checked updates. Re-sync regardless of what was hit.
    grid.addEventListener('click', syncSoon);

    /* Watch the container itself, not the slider.
     *
     * The app swaps in a brand new element, so an observer bound to the old
     * slider would be watching a detached node. Watching childList here catches
     * the replacement; watching the subtree catches Flickity re-enabling on it.
     */
    if (window.MutationObserver) {
      var obs = new MutationObserver(function () {
        if (!window.matchMedia(GRID_MQ).matches) return;
        var s = liveSlider();
        if (s && (s.classList.contains('flickity-enabled') || s.style.height)) {
          sync();
        }
      });
      obs.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    // Crossing the breakpoint: going up we tear Flickity down, going down we
    // must give the carousel back. Neither the theme nor the app exposes a
    // re-init hook we can call, so a reload is the honest way to restore it. It
    // only fires on a real breakpoint crossing, which is rare in normal use.
    var wasDesktop = window.matchMedia(GRID_MQ).matches;
    function onBreakpoint() {
      var isDesktop = window.matchMedia(GRID_MQ).matches;
      if (isDesktop === wasDesktop) return;
      wasDesktop = isDesktop;
      if (isDesktop) {
        sync();
      } else {
        window.location.reload();
      }
    }
    var mq = window.matchMedia(GRID_MQ);
    if (mq.addEventListener) mq.addEventListener('change', onBreakpoint);
    else if (mq.addListener) mq.addListener(onBreakpoint);
  }

  /* ---------------------------------------------------------------
   * 2. Sole section: insole in / out
   * ------------------------------------------------------------- */
  function soleInit() {
    var roots = document.querySelectorAll('[data-obf-sole]');
    Array.prototype.forEach.call(roots, function (root) {
      var btns = root.querySelectorAll('[data-obf-sole-btn]');
      if (!btns.length) return;

      var data = { in: {}, out: {} };
      var holder = root.parentNode ? root.parentNode.querySelector('[data-obf-sole-data]') : null;
      if (!holder) holder = document.querySelector('[data-obf-sole-data]');
      if (holder) {
        try { data = JSON.parse(holder.textContent) || data; } catch (e) { /* keep defaults */ }
      }

      var val = root.querySelector('[data-obf-sole-val]');
      var bestFor = root.querySelector('[data-obf-sole-bestfor]');
      var insole = root.querySelector('[data-obf-sole-insole]');

      function select(which) {
        Array.prototype.forEach.call(btns, function (b) {
          var on = b.getAttribute('data-obf-sole-btn') === which;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        Array.prototype.forEach.call(root.querySelectorAll('[data-obf-sole-state]'), function (s) {
          s.hidden = s.getAttribute('data-obf-sole-state') !== which;
        });
        if (insole) insole.classList.toggle('is-out', which === 'out');
        if (data[which]) {
          if (val && data[which].stack) val.textContent = data[which].stack;
          if (bestFor && data[which].bestFor) bestFor.textContent = data[which].bestFor;
        }
      }

      Array.prototype.forEach.call(btns, function (b) {
        b.addEventListener('click', function () {
          select(b.getAttribute('data-obf-sole-btn'));
        });
      });
    });
  }

  /* ---------------------------------------------------------------
   * 3. Lift count calculator
   *
   * lb per day = (yourOz - ourOz) * steps / 16, floored at zero.
   * ------------------------------------------------------------- */
  function liftInit() {
    var roots = document.querySelectorAll('[data-obf-lc]');
    Array.prototype.forEach.call(roots, function (root) {
      var range = root.querySelector('[data-obf-lc-range]');
      var ozOut = root.querySelector('[data-obf-lc-oz]');
      var lbOut = root.querySelector('[data-obf-lc-lb]');
      var note = root.querySelector('[data-obf-lc-note]');
      if (!range || !lbOut) return;

      var steps = parseFloat(root.getAttribute('data-steps')) || 7000;
      var ourOz = parseFloat(root.getAttribute('data-our-oz')) || 7;

      function render() {
        var yourOz = parseFloat(range.value) || 0;
        var diff = Math.max(0, yourOz - ourOz);
        var lb = Math.round((diff * steps) / 16);

        if (ozOut) ozOut.textContent = yourOz.toFixed(1).replace(/\.0$/, '');
        lbOut.textContent = lb.toLocaleString('en-US');

        if (note) {
          if (lb < 200) {
            note.textContent =
              'That is close to what we weigh already. Most sneakers are heavier than people expect, so it is worth checking yours on a scale.';
          } else {
            var people = Math.max(1, Math.round(lb / 165));
            note.textContent =
              'About the weight of ' + people + (people === 1 ? ' adult' : ' adults') +
              ' that your legs never have to move, every day, without changing anything about how you walk.';
          }
        }
      }

      range.addEventListener('input', render);
      range.addEventListener('change', render);
      render();
    });
  }

  function boot() {
    galleryInit();
    soleInit();
    liftInit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
